import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorMessage } from '@/lib/apiError';
import { useAuth } from '@/context/AuthContext';
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * El enlace del correo (entrega 54): pone la contrasena nueva. El enlace vale
 * una hora y una sola vez (backend/services/contrasena.py).
 */
const ResetPasswordPage = () => {
  const { logout } = useAuth();
  // El codigo viene despues de "#" (no llega al servidor ni a sus registros). Se
  // lee una vez y se saca de la direccion, asi no queda en el historial.
  const [codigo] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
  useEffect(() => {
    if (window.location.hash || window.location.search) {
      window.history.replaceState(window.history.state, '', '/reset-password');
    }
  }, []);

  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [lista, setLista] = useState(false);
  const [error, setError] = useState('');

  const guardar = async (e) => {
    e.preventDefault();
    if (nueva !== repetida) { setError('The two passwords do not match.'); return; }
    setGuardando(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token: codigo, new_password: nueva });
      // Si este navegador tenia una sesion abierta, ya no sirve: se cierra, y
      // "Sign in" lleva al login en vez de a un dashboard que da 401.
      logout();
      setLista(true);
    } catch (err) {
      setError(apiErrorMessage(err.response?.data, 'Could not set the password. Try again.'));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarCampo = (setter) => (e) => { setter(e.target.value); setError(''); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-in" data-testid="reset-password-page">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Set a new password</h1>

        {lista ? (
          <>
            <div
              role="status"
              className="flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800 mt-6"
              data-testid="reset-password-done">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Your password is set. Sign in with it.</span>
            </div>
            <Link to="/login">
              <Button className="w-full h-12 mt-6" data-testid="reset-go-login">
                Sign in <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </>
        ) : !codigo ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm text-rose-700 mt-6"
            data-testid="reset-password-no-link">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              This link is incomplete. <Link to="/forgot-password" className="font-medium underline">Ask for a new one</Link>.
            </span>
          </div>
        ) : (
          <>
            <p className="text-slate-500 mb-8">Use at least 8 characters.</p>
            <form onSubmit={guardar} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nueva">New password</Label>
                <Input id="nueva" type="password" autoComplete="new-password" value={nueva}
                  onChange={cambiarCampo(setNueva)} required className="h-12" data-testid="reset-new-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repetida">Repeat the new password</Label>
                <Input id="repetida" type="password" autoComplete="new-password" value={repetida}
                  onChange={cambiarCampo(setRepetida)} required className="h-12" data-testid="reset-repeat-input" />
              </div>

              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm text-rose-700"
                  data-testid="reset-password-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {error}{' '}
                    {error.includes('Ask for a new one') && (
                      <Link to="/forgot-password" className="font-medium underline">Get a new link</Link>
                    )}
                  </span>
                </div>
              )}

              <Button type="submit" className="w-full h-12 btn-press" disabled={guardando} data-testid="reset-password-submit">
                {guardando ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Set password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
